'use client';
import { getTemplateImageRegenerateUrl, getTemplateImageUrl } from '@components/Modules/TemplatesModule/utils/urls';
import { useClientConfig } from '@providers/ClientConfigProvider';
import cn from '@utils/cn';
import Image from 'next/image';
import { useEffect, useState } from 'react';

type DesignSetImageProps = {
  hash: string;
  name?: string;
  width?: number;
  height?: number;
};

export default function DesignSetImage({ hash, name, width, height }: DesignSetImageProps) {
  const config = useClientConfig();
  const [numberOfRetries, setNumberOfRetries] = useState(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [src, setSrc] = useState(getTemplateImageUrl(hash, config.CDN_URL, 'medium'));

  function onError() {
    if (numberOfRetries >= 10) {
      return;
    }

    if (numberOfRetries === 0) {
      setSrc(getTemplateImageRegenerateUrl(hash, config.RESIZE_URL, 'medium'));
      return setNumberOfRetries((count) => count + 1);
    }

    setTimeoutId(
      setTimeout(() => {
        setSrc(getTemplateImageUrl(hash, config.CDN_URL, 'medium'));
        setNumberOfRetries((count) => count + 1);
      }, 500),
    );
  }

  // cleanup running timeouts
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (
    <Image
      className={cn(
        'block h-full w-full overflow-hidden object-cover',
        'rounded-[4px] sm:rounded-[8px] md:rounded-[4px] lg:rounded-[8px]',
      )}
      src={src}
      alt={name ?? ''}
      loading="lazy"
      width={width}
      height={height}
      onError={onError}
    />
  );
}
